<script setup lang="ts">
  import { IconAccount } from '@gat-ship/icons'
  import type { TenantUser } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { useTenantUserApiCalls } from '../../composables/useTenantUserApiCalls'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const { checkTenantUserEmailAvailabilityRequest } = useTenantUserApiCalls()
  const description = ref('')

  const changeEmailSchema = z.object({
    userId: z.string().min(1),
    email: z.string().min(1, 'Email is required')
  })
  type ChangeEmailFormValues = z.infer<typeof changeEmailSchema>

  const form = useForm({
    defaultValues: {
      userId: '',
      email: ''
    } as ChangeEmailFormValues,
    validators: {
      onChange: changeEmailSchema
    },
    onSubmit: async ({ value }) => {
      await tenantUserProvider.commands.changeEmail({
        userId: value.userId,
        email: value.email
      })
      isOpen.value = false
    }
  })

  export interface ShowChangeEmailParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowChangeEmailParams) {
    description.value = tenantUser.fullName
    form.reset({
      userId: tenantUser.userId,
      email: tenantUser.email ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAccount class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Change Email
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField
            :form="form"
            name="email"
            label="New Email"
            required="Email is required"
            :validators="{
              onChangeAsync: async ({ value, signal }) => {
                if (!value || value.length < 3) return undefined
                if (!value.includes('@'))
                  return 'Email must contain an @ symbol'
                try {
                  const available =
                    await checkTenantUserEmailAvailabilityRequest(value)
                  return available ? undefined : 'Email is already in use'
                } catch (err) {
                  if ((err as Error).name === 'AbortError') return undefined
                  return 'Something went wrong.'
                }
              },
              onChangeAsyncDebounceMs: 400
            }"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                data-testid="change-email-input-email"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
